import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';

interface RatingDeltaProps {
  delta: number;
  /** Append " SR" so the number reads as a rating rather than a score. */
  showUnit?: boolean;
  size?: 'small' | 'medium';
}

/**
 * A rating change, shown as a signed chip with a direction arrow.
 *
 * Colour alone does not carry the meaning — an arrow does too — so the direction
 * survives colour-blindness and greyscale printing.
 */
const RatingDelta: React.FC<RatingDeltaProps> = ({ delta, showUnit = false, size = 'small' }) => {
  const theme = useTheme();
  const rounded = Math.round(delta);

  const { color, Icon } =
    rounded > 0
      ? { color: theme.palette.success.dark, Icon: ArrowUpwardIcon }
      : rounded < 0
        ? { color: theme.palette.error.dark, Icon: ArrowDownwardIcon }
        : { color: theme.palette.text.secondary, Icon: RemoveIcon };

  const label = rounded > 0 ? `+${rounded}` : `${rounded}`;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        px: size === 'small' ? 0.9 : 1.2,
        py: 0.25,
        borderRadius: 999,
        backgroundColor: alpha(color, 0.12),
        color
      }}
    >
      <Icon sx={{ fontSize: size === 'small' ? 13 : 16 }} />
      <Typography
        component="span"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 700,
          fontSize: size === 'small' ? '0.75rem' : '0.875rem'
        }}
      >
        {label}
        {showUnit ? ' SR' : ''}
      </Typography>
    </Box>
  );
};

export default RatingDelta;
